import React, { FC, useCallback } from "react";
import {
  Content,
  ContentWrapper,
  Heading,
} from "containers/RightPanel/RightPanel.styles";
import { ButtonColor, ButtonVariant } from "enums/Button";
import { IconType } from "enums/Icon";
import { List, Map } from "immutable";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { PackingMethodsResponseDto } from "types/api/cart";
import { OpenRightPanel } from "types/common/rightPanel";
import Button from "../../../../components/Button/Button";
import Icon, { IconPng } from "../../../../components/Icon/Icon";
import PackagesList from "../../../../components/PackagesList/PackagesList";
import ServiceLogo from "../../../../components/ServiceLogo/ServiceLogo";
import useFetchOnceEffect from "../../../../hooks/useFetchOnceEffect";
import { openRightPanel } from "../../../../reduxFolder/reducers/rightPanel";
import { getPackingMethods } from "../../../../reduxFolder/reducers/services";
import { panelSelectData } from "../../../../reduxFolder/selectors/rightPanelSelectors";
import { servicesSelectPackingMethods } from "../../../../reduxFolder/selectors/servicesSelectors";
import {
  HeaderTitle,
  InnerSection,
  Section,
  SectionContent,
  SectionHeader,
} from "../../../../styles/outgoingStyles";
import { ChangeButton } from "../../../../styles/parcelsStyles";
import { useDemoHint } from "../../../Storage/components/DemoShipments/useDemoHint";
import {
  Footer,
  PanelAddressItem,
  TabMain,
  Title,
} from "../NotFinishedPanel/NotFinishedPanel.styles";

interface DemoNotFinishedPanelProps {
  panelData: any;
  packingMethods: any;
  openRightPanel: OpenRightPanel;
  getPackingMethods: () => Promise<PackingMethodsResponseDto[]>;
}

// Browse-only counterpart of NotFinishedPanel's details view. The real panel
// renders the redux cart; the demo draft lives only in panelData ("parcel"),
// so nothing leaks into the cart slice, hasItems, or /shipping.
const DemoNotFinishedPanel: FC<DemoNotFinishedPanelProps> = React.memo(
  ({ panelData, packingMethods, openRightPanel, getPackingMethods }) => {
    const { t } = useTranslation("common");
    const showDemoHint = useDemoHint();

    // Read-only reference fetch (same as the real panel) — the packing title
    // needs the partner's packing methods list.
    useFetchOnceEffect(!packingMethods.size, getPackingMethods);

    const parcel = panelData.get("parcel", Map());
    const sku = parcel.get("sku");
    const items = parcel.get("items", List());
    const destinationAddress = parcel.get("destination_address", Map());
    const delivery = parcel.get("preferred_carrier");
    const packingCode = parcel.getIn(["optional_line_item_codes", 0]);

    const packing = packingMethods.find(
      (packingMethod: any) => packingMethod.get("code") === packingCode,
    );
    const packingTitle = (packing?.get("title") as string) ?? "";

    // isDemo makes StorageItemPanel browse-only (no fetches, hint-stub
    // mutations); back returns to this panel with the same panelData.
    const clickPackagesItem = useCallback(
      (item: any) => {
        openRightPanel(
          "STORAGE_ITEM",
          Map({
            readOnly: true,
            isDemo: true,
            id: item.get("shipment_id"),
            originalItem: item,
            detailedItemID: parcel.get("id"),
            consolidationSku: sku,
            emblem_thumb_url: item.get("emblem_thumb_url"),
            description: item.get("shipment_tracking_number"),
            returnPreviousPanel: "DEMO_NOT_FINISHED",
            rootReturnPanel: "DEMO_NOT_FINISHED",
            parcel,
            isCartVariant: true,
            isItemInCart: true,
          }),
        );
      },
      [openRightPanel, parcel, sku],
    );

    return (
      <ContentWrapper>
        <Heading>
          <Title>{sku}</Title>
        </Heading>
        <TabMain>
          <Content>
            <Section>
              <SectionHeader>
                <HeaderTitle>
                  <Icon type={IconType.Address} />{" "}
                  {t("parcels.shippingAddress")}
                </HeaderTitle>
                <ChangeButton
                  onClick={showDemoHint}
                >
                  {t("common.change")}
                </ChangeButton>
              </SectionHeader>
              <PanelAddressItem address={destinationAddress} isHideButtons />
            </Section>
            <Section>
              <SectionHeader>
                <HeaderTitle>
                  <IconPng type="turbine" /> {t("parcels.shippingMethod")}
                </HeaderTitle>
                <ChangeButton
                  onClick={showDemoHint}
                >
                  {t("common.change")}
                </ChangeButton>
              </SectionHeader>
              <InnerSection>
                <SectionContent>
                  <ServiceLogo serviceKey={delivery} />
                  {delivery}
                </SectionContent>
              </InnerSection>
            </Section>
            <Section>
              <SectionHeader>
                <HeaderTitle>
                  <IconPng type="tape" /> {t("parcels.packingGrade")}
                </HeaderTitle>
                <ChangeButton
                  onClick={showDemoHint}
                >
                  {t("common.change")}
                </ChangeButton>
              </SectionHeader>
              {packingCode && (
                <InnerSection>
                  <SectionContent>
                    <ServiceLogo serviceKey={packingCode} />
                    {packingTitle}
                  </SectionContent>
                </InnerSection>
              )}
            </Section>
            <Section>
              <SectionHeader>
                <HeaderTitle>
                  <Icon type={IconType.Package} />
                  {`${t("parcels.shipments")} (${items.count()})`}
                </HeaderTitle>
              </SectionHeader>
              <InnerSection>
                <SectionContent>
                  <PackagesList
                    extended
                    items={items}
                    handleClick={clickPackagesItem}
                  />
                </SectionContent>
              </InnerSection>
            </Section>
          </Content>
          <Footer>
            <Button color={ButtonColor.Danger} onClick={showDemoHint}>
              {t("common.cancelAndDelete")}
            </Button>
            <Button
              color={ButtonColor.Primary}
              variant={ButtonVariant.Filled}
              onClick={showDemoHint}
            >
              {t("common.continue")}
            </Button>
          </Footer>
        </TabMain>
      </ContentWrapper>
    );
  },
);

const withConnect = connect(
  createStructuredSelector({
    panelData: panelSelectData,
    packingMethods: servicesSelectPackingMethods,
  }),
  { openRightPanel, getPackingMethods },
);

export default withConnect(DemoNotFinishedPanel);
